import { useEffect, useState } from "react";
import { CircularProgress, Stack, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { db } from "firebaseConfig";
import { GenericConverter } from "utils/firestoreConverters";
import { ProcessedListingData } from "../../../@types/sharedSchema";
import { ListingDetailsDialog } from "components/_dashboard/listings/modal/dialog/ListingDetailsDialog";
import type { AlgoliaHitResult } from "components/_dashboard/listingsMatch/algoliaClient";

// `yad2-listings-latest` is the CANONICAL processed-listing collection (webot + yad2 +
// madlan all live here, keyed by doc id = Algolia objectID). Makeovers are keyed by that
// SAME doc id, so resolving a listing is a direct doc-id lookup — no originalListingId.
const processedListingsCollection = () =>
  db.collection("yad2-listings-latest").withConverter(GenericConverter<ProcessedListingData>());

/** Resolve a listing by its `yad2-listings-latest` doc id (= the makeover's `listingId`). */
async function fetchProcessedListing(listingId: string): Promise<AlgoliaHitResult | null> {
  const snap = await processedListingsCollection().doc(listingId).get();
  if (snap.exists) return { ...(snap.data() as ProcessedListingData), objectID: listingId } as AlgoliaHitResult;

  console.warn(`[makeovers] listing not found in yad2-listings-latest for listingId=${listingId}`);
  return null;
}

type ListingTitleLinkProps = {
  /** The `yad2-listings-latest` doc id (= the makeover's `listingId`). */
  listingId: string;
  /** Optional count shown in parentheses after the title (e.g. number of makeovers). */
  count?: number;
};

/**
 * Lazily resolves a listing FROM FIRESTORE in the background and renders its address
 * as a clickable title that opens the listing-details dialog. While it loads the title
 * shows a spinner; once loaded the title becomes clickable. Shared by the makeover
 * studio results header and the "previous makeovers" per-listing groups so both behave
 * identically.
 */
export function ListingTitleLink({ listingId, count }: ListingTitleLinkProps): JSX.Element {
  const { t: translate } = useTranslation();
  const [listing, setListing] = useState<AlgoliaHitResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchProcessedListing(listingId)
      .then((found) => {
        if (active) setListing(found);
      })
      .catch((err) => console.warn(`[makeovers] error resolving listingId=${listingId}`, err))
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [listingId]);

  const addressLabel = listing?.address
    ? [listing.address.street, listing.address.neighborhood, listing.address.city].filter(Boolean).join(", ")
    : "";

  if (loading) {
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <CircularProgress size={14} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }} color="text.secondary">
          {translate("makeovers.loadingListing", "טוען נכס...")}
        </Typography>
      </Stack>
    );
  }

  return (
    <>
      <Stack direction="row" alignItems="baseline" gap={0.75} flexWrap="wrap">
        {listing ? (
          <Typography
            variant="subtitle1"
            onClick={() => setDialogOpen(true)}
            sx={{ fontWeight: 700, color: "primary.main", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
          >
            {addressLabel || translate("makeovers.aListing", "נכס")}
          </Typography>
        ) : (
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {translate("makeovers.aListing", "נכס")}
          </Typography>
        )}
        {count != null && (
          <Typography component="span" variant="body2" color="text.secondary">
            ({count})
          </Typography>
        )}
      </Stack>

      {listing && (
        <ListingDetailsDialog open={dialogOpen} algoliaListing={listing} handleClose={() => setDialogOpen(false)} />
      )}
    </>
  );
}

export default ListingTitleLink;
